/**
 * Kochmodus: Schritt für Schritt durch das Rezept.
 *
 * Große Schrift, ein Schritt pro Bildschirm, Timer für die Schritte und eine
 * Prüfliste vorab. Der Bildschirm bleibt an, damit man die Hände frei hat.
 */
import { formatMinutes, preheatHint, stepPlainText, warmUpIngredients } from '@cookbook/domain';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { RecipeDetail } from '../api/types.ts';
import { useQuery } from '../api/useQuery.ts';
import { AppIcon } from '../components/AppIcon.tsx';
import { CheckItem } from '../components/CheckItem.tsx';
import { IngredientList } from '../components/IngredientList.tsx';
import { ScaleControl } from '../components/ScaleControl.tsx';
import { Stars } from '../components/Stars.tsx';
import { StepAnimation } from '../components/StepAnimation.tsx';
import { StepMedia } from '../components/StepMedia.tsx';
import { StepText } from '../components/StepText.tsx';
import { Timer } from '../components/Timer.tsx';
import { useSpeech } from '../lib/useSpeech.ts';
import { useWakeLock } from '../lib/useWakeLock.ts';
import { sendOrQueue } from '../state/outbox.ts';
import { useScaling } from '../state/useScaling.ts';
import { texts } from '../texts/de.ts';

/** Merkt sich, ob automatisch vorgelesen wird. */
const AUTO_READ_KEY = 'kochbuch.vorlesen';

/** Kochmodus eines Rezepts. */
export function CookModePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<{ recipe: RecipeDetail }>(
    id === '' ? null : `/api/recipes/${id}`,
  );
  const [stage, setStage] = useState<'prepare' | 'cook' | 'done'>('prepare');
  const [stepIndex, setStepIndex] = useState(0);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [rating, setRating] = useState<number | null>(null);
  const [note, setNote] = useState('');

  useWakeLock(true);

  const recipe = data?.recipe ?? null;
  const scaling = useScaling(recipe);
  const speech = useSpeech();
  const [autoRead, setAutoRead] = useState(() => {
    try {
      return window.localStorage.getItem(AUTO_READ_KEY) === 'ja';
    } catch {
      return false;
    }
  });

  /** Text eines Schritts zum Vorlesen, mit umgerechneten Mengen. */
  function stepSpeech(index: number): string {
    const current = recipe?.steps[index];
    if (recipe === null || current === undefined) {
      return '';
    }
    const body = stepPlainText(current.instruction, recipe.ingredients, scaling.factor, scaling.unitMode);
    const duration =
      current.durationMinutes === null ? '' : ` Dauer: ${formatMinutes(current.durationMinutes)}`;
    return `${texts.cook.step} ${index + 1}. ${current.title === '' ? '' : `${current.title}. `}${body}${duration}`;
  }

  // Beim Wechsel des Schritts automatisch vorlesen, wenn gewünscht.
  useEffect(() => {
    if (autoRead && stage === 'cook') {
      speech.speak(stepSpeech(stepIndex));
    }
    // Nur auf den Schrittwechsel reagieren, nicht auf jede neue Funktion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, stage, autoRead]);

  /** Schaltet ein Häkchen um. */
  function toggle(key: string) {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  /** Schließt das Kochen ab und protokolliert es. */
  async function finish(recipeId: string) {
    try {
      const title = recipe?.title ?? 'Rezept';
      // Auch ohne Netz geht nichts verloren: Protokoll und Bewertung warten dann.
      await sendOrQueue({
        key: `cooked:${recipeId}:${new Date().toISOString()}`,
        method: 'POST',
        path: `/api/recipes/${recipeId}/cooked`,
        body: { servings: scaling.amount, note: note === '' ? null : note },
        label: `${texts.cook.finishedTitle} ${title}`,
      });
      if (rating !== null) {
        await sendOrQueue({
          key: `rating:${recipeId}`,
          method: 'PUT',
          path: `/api/recipes/${recipeId}/rating`,
          body: { stars: rating, comment: null },
          label: `${texts.detail.myRating}: ${title} (${rating})`,
        });
      }
      void navigate(`/rezepte/${recipeId}${scaling.search}`);
    } catch {
      window.alert('Das Speichern ist fehlgeschlagen. Bitte erneut versuchen.');
    }
  }

  if (loading) {
    return <p className="p-6 text-content-muted">{texts.common.loading}</p>;
  }
  if (error !== null || recipe === null) {
    return (
      <div className="p-6">
        <p className="rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">
          {error ?? 'Das Rezept gibt es nicht.'}
        </p>
        <Link to="/rezepte" className="mt-3 inline-block text-sm text-accent underline">
          {texts.common.back}
        </Link>
      </div>
    );
  }

  const step = recipe.steps[stepIndex];
  const isLast = stepIndex === recipe.steps.length - 1;

  return (
    <div className="flex min-h-full flex-col bg-surface">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border-subtle bg-surface/95 px-4 py-3 backdrop-blur">
        <button
          type="button"
          onClick={() => {
            if (window.confirm(texts.cook.exitConfirm)) {
              void navigate(`/rezepte/${recipe.id}${scaling.search}`);
            }
          }}
          className="text-sm text-content-muted"
        >
          {texts.cook.exit}
        </button>
        <span className="min-w-0 flex-1 truncate text-center text-sm font-semibold">{recipe.title}</span>
        <span className="inline-flex items-center gap-1 text-xs text-content-muted">
          <AppIcon name="clock" size={14} />
          {texts.cook.keepAwake}
        </span>
      </header>

      {stage === 'prepare' ? (
        <section className="mx-auto w-full max-w-2xl flex-1 px-4 py-6">
          <h1 className="text-2xl font-bold">{texts.cook.miseEnPlace}</h1>
          <p className="mt-1 mb-4 text-content-muted">{texts.cook.miseIntro}</p>

          <div className="mb-4">
            <ScaleControl recipe={recipe} scaling={scaling} />
          </div>

          {warmUpIngredients(recipe.ingredients).length === 0 ? null : (
            <p className="mb-4 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">
              <strong>{texts.cook.warmUp}:</strong> {warmUpIngredients(recipe.ingredients).join(', ')}
            </p>
          )}

          <h2 className="mb-2 text-sm font-semibold text-content-muted">{texts.detail.ingredients}</h2>
          <div className="mb-5">
            <IngredientList
              ingredients={recipe.ingredients}
              factor={scaling.factor}
              unitMode={scaling.unitMode}
              checked={checked}
              onToggle={toggle}
            />
          </div>

          {recipe.equipment.length === 0 ? null : (
            <>
              <h2 className="mb-2 text-sm font-semibold text-content-muted">{texts.detail.equipment}</h2>
              <div className="mb-5 space-y-2">
                {recipe.equipment.map((item) => {
                  const key = `g-${item}`;
                  return (
                    <CheckItem
                      key={key}
                      label={item}
                      checked={checked.has(key)}
                      onToggle={() => toggle(key)}
                    />
                  );
                })}
              </div>
            </>
          )}

          <button
            type="button"
            onClick={() => setStage('cook')}
            className="w-full rounded-2xl bg-accent px-4 py-4 text-lg font-semibold text-on-accent"
          >
            {texts.cook.startCooking}
          </button>
        </section>
      ) : null}

      {stage === 'cook' && step !== undefined ? (
        <section className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-6">
          <p className="mb-1 text-sm text-content-muted">
            {texts.cook.step} {stepIndex + 1} {texts.cook.of} {recipe.steps.length}
          </p>
          <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${((stepIndex + 1) / recipe.steps.length) * 100}%` }}
            />
          </div>

          {speech.supported ? (
            <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
              <button
                type="button"
                onClick={() => (speech.speaking ? speech.stop() : speech.speak(stepSpeech(stepIndex)))}
                aria-pressed={speech.speaking}
                className="rounded-xl border border-border-subtle px-3 py-1.5"
              >
                {speech.speaking ? `■ ${texts.cook.stopReading}` : `🔊 ${texts.cook.readAloud}`}
              </button>
              <label className="flex items-center gap-2 text-content-muted">
                <input
                  type="checkbox"
                  checked={autoRead}
                  onChange={(event) => {
                    setAutoRead(event.target.checked);
                    try {
                      window.localStorage.setItem(AUTO_READ_KEY, event.target.checked ? 'ja' : 'nein');
                    } catch {
                      // Ohne Speicher gilt die Wahl nur für diesen Besuch.
                    }
                  }}
                />
                {texts.cook.autoRead}
              </label>
            </div>
          ) : null}

          {step.section === null ? null : (
            <p className="text-xs tracking-wide text-accent uppercase">{step.section}</p>
          )}
          {step.title === '' ? null : <h1 className="mt-1 text-2xl font-bold">{step.title}</h1>}
          {step.animationTemplateId === null ? null : (
            <StepAnimation
              key={step.id}
              templateId={step.animationTemplateId}
              params={step.animationParams}
              className="mt-3 w-full max-w-md self-center"
            />
          )}
          <StepText
            text={step.instruction}
            ingredients={recipe.ingredients}
            factor={scaling.factor}
            unitMode={scaling.unitMode}
            className="mt-3 text-xl leading-relaxed"
          />
          <StepMedia key={`media-${step.id}`} media={step.media} large className="mt-4" />

          {(() => {
            // Kann der nächste Schritt schon jetzt nebenbei beginnen?
            const next = recipe.steps[stepIndex + 1];
            if (next === undefined || !next.parallel) {
              return null;
            }
            return (
              <p className="mt-4 rounded-xl bg-surface-sunken px-3 py-2 text-sm">
                <strong>{texts.scale.meanwhile}:</strong> {texts.cook.step} {stepIndex + 2}
                {next.title === '' ? '' : ` – ${next.title}`}
              </p>
            );
          })()}

          {(() => {
            const hint = preheatHint(recipe.steps, stepIndex);
            if (hint === null) {
              return null;
            }
            return (
              <p
                role="status"
                className="mt-4 flex items-start gap-2 rounded-xl bg-accent-soft px-3 py-2 text-accent-strong"
              >
                <AppIcon name="flame" size={20} className="mt-0.5 shrink-0" />
                <span>
                  <strong>
                    {texts.cook.preheatNow}: {hint.temperature} °C
                  </strong>{' '}
                  –{' '}
                  {hint.minutesUntil === 0 || hint.stepIndex === stepIndex + 1
                    ? texts.cook.preheatSoon
                    : texts.cook.preheatDetail
                        .replace('{min}', String(hint.minutesUntil))
                        .replace('{step}', String(hint.stepIndex + 1))}
                </span>
              </p>
            );
          })()}

          {step.durationMinutes === null ? null : (
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-content-muted">
              <AppIcon name="clock" size={16} />
              {step.durationMinutes} {texts.detail.minutesShort}
              {step.passive ? ` · ${texts.detail.passiveHint}` : ''}
            </p>
          )}
          {step.temperatureCelsius === null ? null : (
            <p className="mt-2 inline-flex items-center gap-2 text-sm text-content-muted">
              <AppIcon name="flame" size={16} />
              {step.temperatureCelsius} °C
            </p>
          )}

          {step.timer && step.durationMinutes !== null ? (
            <Timer minutes={step.durationMinutes} label={step.title === '' ? texts.cook.title : step.title} />
          ) : null}

          {step.whyItWorks === null ? null : (
            <details className="mt-3 rounded-xl bg-surface-sunken p-3 text-sm">
              <summary className="cursor-pointer font-medium">{texts.cook.whyItWorks}</summary>
              <p className="mt-2 text-content-muted">{step.whyItWorks}</p>
            </details>
          )}
          {step.doneWhen === null ? null : (
            <details className="mt-2 rounded-xl bg-surface-sunken p-3 text-sm">
              <summary className="cursor-pointer font-medium">{texts.cook.doneWhen}</summary>
              <p className="mt-2 text-content-muted">{step.doneWhen}</p>
            </details>
          )}

          <div className="mt-auto flex gap-2 pt-6">
            <button
              type="button"
              onClick={() => setStepIndex((value) => Math.max(0, value - 1))}
              disabled={stepIndex === 0}
              className="rounded-2xl border border-border-subtle px-5 py-4 text-lg disabled:opacity-40"
            >
              {texts.cook.previous}
            </button>
            <button
              type="button"
              onClick={() => {
                if (isLast) {
                  setStage('done');
                } else {
                  setStepIndex((value) => value + 1);
                }
              }}
              className="flex-1 rounded-2xl bg-accent px-5 py-4 text-lg font-semibold text-on-accent"
            >
              {isLast ? texts.cook.finish : texts.cook.next}
            </button>
          </div>
        </section>
      ) : null}

      {stage === 'done' ? (
        <section className="mx-auto w-full max-w-2xl flex-1 px-4 py-8 text-center">
          <AppIcon name="hat" size={48} className="mx-auto text-accent" />
          <h1 className="mt-3 text-2xl font-bold">{texts.cook.finishedTitle}</h1>
          <p className="mt-1 text-content-muted">{texts.cook.finishedIntro}</p>

          <div className="mt-5 flex justify-center">
            <Stars value={rating} editable size={36} onChange={setRating} />
          </div>

          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={texts.detail.commentPlaceholder}
            rows={3}
            className="mt-5 w-full rounded-xl border border-border-subtle bg-surface-raised px-3 py-2 text-left text-sm"
          />

          <button
            type="button"
            onClick={() => void finish(recipe.id)}
            className="mt-5 w-full rounded-2xl bg-accent px-4 py-4 text-lg font-semibold text-on-accent"
          >
            {texts.cook.saveAndFinish}
          </button>
          <button
            type="button"
            onClick={() => void navigate(`/rezepte/${recipe.id}${scaling.search}`)}
            className="mt-2 w-full rounded-2xl border border-border-subtle px-4 py-3 text-sm"
          >
            {texts.cook.skipRating}
          </button>
        </section>
      ) : null}
    </div>
  );
}
